import React, { memo, useCallback } from "react";
import { Card, Button, Form } from "react-bootstrap";
import { DomainCompetency } from "Stores/courseStore";
import { CaretDownIcon } from "Components/Icons/CaretDown";
import { CaretRightIcon } from "Components/Icons/CaretRight";

interface DomainStandardCompetenciesItemProps {
    competency: DomainCompetency;
    level: number;
    selectedId: string;
    openMap: Record<string, boolean>;
    toggleOpen: (ruuid: string) => void;
    onItemClick: (ruuid: string) => void;
    toggleEnabled: (ruuid: string) => void;
    isChecked: (competency: DomainCompetency) => boolean;
}

const DomainStandardCompetenciesItem = ({
    competency,
    level,
    selectedId,
    openMap,
    toggleOpen,
    onItemClick,
    toggleEnabled,
    isChecked,
}: DomainStandardCompetenciesItemProps) => {
    const isOpen = openMap[competency.ruuid] ?? false;
    const hasChildren = competency.children.length > 0;
    const checked = isChecked(competency);

    const handleToggleOpen = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        toggleOpen(competency.ruuid);
    }, [competency.ruuid, toggleOpen]);

    const handleToggleEnabled = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        toggleEnabled(competency.ruuid);
    }, [competency.ruuid, toggleEnabled]);

    const handleItemClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onItemClick(competency.ruuid);
    }, [competency.ruuid, onItemClick]);

    return (
        <Card
            id={competency.ruuid}
            className={`domain-competencies-item level-${level} ${selectedId === competency.ruuid ? "selected" : ""}`}
            onClick={handleItemClick}
        >
            <Card.Header onClick={(e) => e.stopPropagation()}>
                <div className="item-left-section">
                    {hasChildren && (
                        <Button
                            variant="link"
                            onClick={handleToggleOpen}
                            className="expand-button"
                        >
                            {isOpen ? (
                                <CaretDownIcon className="drop-icon" size="14" />
                            ) : (
                                <CaretRightIcon className="drop-icon" size="14" />
                            )}
                        </Button>
                    )}

                    <span>{competency.standard_name}</span>
                </div>

                <div className="toggleContainer">
                    <Form.Switch
                        id={`switch-${competency.ruuid}`}
                        checked={checked}
                        onChange={handleToggleEnabled}
                        className="enabled-switch"
                        disabled={!hasChildren}
                    />
                </div>
            </Card.Header>

            {isOpen && hasChildren && (
                <Card.Body className="children-body">
                    {competency.children.map((child) => (
                        <MemoizedDomainStandardCompetenciesItem
                            key={child.ruuid}
                            competency={child}
                            level={level + 1}
                            selectedId={selectedId}
                            openMap={openMap}
                            toggleOpen={toggleOpen}
                            onItemClick={onItemClick}
                            toggleEnabled={toggleEnabled}
                            isChecked={isChecked}
                        />
                    ))}
                </Card.Body>
            )}
        </Card>
    );
};

export const MemoizedDomainStandardCompetenciesItem = memo(DomainStandardCompetenciesItem);
